import {
  Flex,
  Form,
  type FormInstance,
  Input,
  Select,
  theme,
  Typography,
  Upload
} from 'antd'
import { FolderMinus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Text } from 'src/components'

import { useTicketAttachmentUpload } from '../hooks'
import type { TicketFormState } from '../types'

import { PRIORITY_LABEL_KEYS, PriorityLabel } from './PriorityLabel'

const { Link } = Typography
const { Dragger } = Upload

type Props = {
  form: FormInstance<TicketFormState>
}

export const TicketForm = ({ form }: Props) => {
  const { token } = theme.useToken()
  const uploadAttachment = useTicketAttachmentUpload()
  const { t } = useTranslation()

  const priorityOptions = ([1, 2, 3, 4] as const).map((value) => ({
    value,
    title: t(PRIORITY_LABEL_KEYS[value]),
    label: <PriorityLabel priority={value} />
  }))

  return (
    <Form form={form} layout="vertical" initialValues={{ priority: 3 }}>
      <Form.Item
        name="title"
        label={t('tickets.ticket')}
        rules={[{ required: true }]}
        required
      >
        <Input placeholder={t('tickets.example')} />
      </Form.Item>

      <Form.Item
        name="priority"
        label={t('tickets.priority.label')}
        rules={[{ required: true }]}
      >
        <Select options={priorityOptions} />
      </Form.Item>

      <Form.Item name="description" label={t('tickets.description')}>
        <Input.TextArea
          placeholder={t('tickets.example_description')}
          rows={5}
        />
      </Form.Item>

      <Form.Item
        name="attachments"
        label={t('tickets.attachments')}
        valuePropName="fileList"
        getValueFromEvent={(e) => e?.fileList}
      >
        <Dragger customRequest={uploadAttachment} multiple pastable>
          <Flex vertical gap={16} align="center">
            <FolderMinus color={token.colorPrimary} size={48} />

            <Flex vertical gap={4}>
              <div>
                <Text size={16}>{t('tickets.dragAndDrop')}</Text>{' '}
                <Link>{t('tickets.selectFile')}</Link>
              </div>

              <Text type="secondary">{t('tickets.uploadHint')}</Text>
            </Flex>
          </Flex>
        </Dragger>
      </Form.Item>
    </Form>
  )
}
